import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

// Format minutes as mm:ss
function formatMinSec(minutes: number): string {
  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

// Provider colors
const COLORS: Record<string, string> = {
  assemblyai: "#3B82F6",
  claude_haiku: "#8B5CF6",
  gemini_flash: "#10B981",
  logged: "#F59E0B",
};

const PROVIDER_LABELS: Record<string, string> = {
  assemblyai: "AssemblyAI (logged)",
  assemblyai_derived: "AssemblyAI (from duration)",
  claude_haiku: "Claude Haiku",
  gemini_flash: "Gemini Flash",
};

/* ── Types matching Henry's actual endpoint response ── */

interface UnifiedCosts {
  period: string;
  totals: {
    period_cost: number;
    logged_llm_costs: number;
    derived_assemblyai_cost: number;
    period_minutes: number;
    period_items: number;
    avg_queue_wait_seconds?: number | null;
  };
  all_time: {
    total_cost: number;
    logged_costs: number;
    derived_assemblyai: number;
    total_minutes: number;
    total_items: number;
    cost_per_minute: number;
    // Unique minutes (actual transcriptions)
    unique_minutes: number;
    unique_transcripts: number;
    true_cost: number;
    true_cost_per_minute: number;
    effective_cost_per_minute: number;
    cache_hit_rate_pct: number;
  };
  by_provider: Record<
    string,
    {
      cost: number;
      calls?: number;
      input_units?: number;
      output_units?: number;
      minutes?: number;
      rate_per_minute?: number;
    }
  >;
  by_day: {
    date: string;
    logged_cost: number;
    derived_assemblyai: number;
    total_cost: number;
    minutes_transcribed: number;
    min_per_hour?: number | null;
    jobs_completed?: number | null;
  }[];
  processing_stats: {
    jobs_with_timing: number;
    avg_processing_seconds: number;
    total_audio_minutes: number;
    avg_audio_per_job_min: number;
    realtime_speed: number;
    minutes_per_hour_audio: number;
  } | null;
  data_quality: {
    total_items: number;
    items_with_duration: number;
    items_missing_duration: number;
    duration_coverage_pct: number;
    jobs_with_cost_tracking: number;
  };
  rates: {
    assemblyai_per_minute: number;
    note: string;
  };
  waste: {
    failed_jobs: number;
    failed_minutes: number;
    failed_cost: number;
    total_jobs: number;
    total_minutes_submitted: number;
    failure_rate_pct: number;
  };
  queue?: {
    active: number;
    waiting: number;
    max: number;
    avg_wait_seconds: number;
  };
}

interface AdminCostsViewProps {
  costsPeriod: string;
  setCostsPeriod: (p: "day" | "week" | "month" | "all") => void;
}

export default function AdminCostsView({
  costsPeriod,
  setCostsPeriod,
}: AdminCostsViewProps) {
  const [data, setData] = useState<UnifiedCosts | null>(null);
  const [todayData, setTodayData] = useState<UnifiedCosts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch selected period
      const res = await fetch(
        `${API_BASE}/admin/costs/unified?period=${costsPeriod}`,
        { headers: { "X-Admin-Key": ADMIN_KEY } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      
      // Always fetch today's data for bottom boxes (if not already fetching today)
      if (costsPeriod !== "day") {
        const todayRes = await fetch(
          `${API_BASE}/admin/costs/unified?period=day`,
          { headers: { "X-Admin-Key": ADMIN_KEY } }
        );
        if (todayRes.ok) {
          const todayJson = await todayRes.json();
          setTodayData(todayJson);
        }
      } else {
        setTodayData(json);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [costsPeriod]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">API Costs</h2>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
          <button
            onClick={fetchCosts}
            className="ml-3 font-medium underline hover:text-red-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { totals, all_time, by_provider, by_day, processing_stats, data_quality } =
    data;

  // Build provider list for display
  const providers = Object.entries(by_provider)
    .map(([key, val]) => ({ key, ...val }))
    .sort((a, b) => b.cost - a.cost);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Dashboard</h2>
        <select
          value={costsPeriod}
          onChange={(e) =>
            setCostsPeriod(e.target.value as "day" | "week" | "month" | "all")
          }
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="all">All Time</option>
        </select>
        <button
          onClick={fetchCosts}
          className="rounded-md border border-gray-300 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          title="Refresh"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-4 w-4"
          >
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H4.598a.75.75 0 0 0-.75.75v3.634a.75.75 0 0 0 1.5 0v-2.033l.312.311a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.06-7.19a.75.75 0 0 0-1.5 0v2.033l-.312-.312A7 7 0 0 0 2.848 9.093a.75.75 0 0 0 1.449.39A5.5 5.5 0 0 1 13.89 7.11l.311.312h-2.432a.75.75 0 0 0 0 1.5h3.634a.75.75 0 0 0 .75-.75V4.538Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Row 1: Period-specific Cost + Speed + Queue Wait */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          label="Cost per Minute"
          value={totals.period_minutes > 0 ? `$${(totals.period_cost / totals.period_minutes).toFixed(4)}` : "—"}
          sub={totals.period_minutes > 0 ? `${costsPeriod === "day" ? "today" : costsPeriod === "week" ? "this week" : costsPeriod === "month" ? "this month" : "all time"}` : "No data this period"}
          variant="primary"
        />
        <StatCard
          label="Speed"
          value={processing_stats && totals.period_items > 0 ? formatMinSec(processing_stats.minutes_per_hour_audio) : "—"}
          sub={processing_stats && totals.period_items > 0 ? `${processing_stats.realtime_speed.toFixed(0)}x realtime · per 1hr audio` : "No data this period"}
          variant="primary"
        />
        <PeriodQueueWaitCard avgWaitSeconds={totals.avg_queue_wait_seconds} periodLabel={costsPeriod === "day" ? "today" : costsPeriod === "week" ? "this week" : costsPeriod === "month" ? "this month" : "all time"} />
      </div>

      {/* Row 2: Period Totals (secondary cards - lighter blue) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          label="Total Cost"
          value={totals.period_cost > 0 ? `$${totals.period_cost.toFixed(2)}` : "—"}
          sub={totals.period_cost > 0 ? `${totals.period_items} transcripts` : "No spend this period"}
          variant="secondary"
        />
        <StatCard
          label="Hours Transcribed"
          value={totals.period_minutes > 0 ? (totals.period_minutes / 60).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}
          sub={totals.period_items > 0 ? `${totals.period_items} transcripts` : "No transcripts this period"}
          variant="secondary"
        />
        <StatCard
          label="Failed Jobs"
          value={`${data.waste?.failed_jobs || 0}`}
          sub={data.waste?.failed_jobs 
            ? `${data.waste.failure_rate_pct}% rate${data.waste.failed_cost > 0 ? ` · $${data.waste.failed_cost.toFixed(2)} wasted` : ""}`
            : "0% failure rate"}
          variant="secondary"
        />
      </div>

      {/* Divider: Today section */}
      <div className="flex items-center gap-3 pt-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Today</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Row 3: Always-visible Today's metrics */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          label="Avg Cost"
          value={todayData && todayData.totals.period_minutes > 0 
            ? `$${(todayData.totals.period_cost / todayData.totals.period_minutes).toFixed(4)}`
            : "—"}
          sub={todayData && todayData.totals.period_items > 0 
            ? `per minute · ${todayData.totals.period_items} transcripts today`
            : "No transcripts today"}
          variant="tertiary"
        />
        <StatCard
          label="Avg Speed"
          value={todayData?.processing_stats && todayData.totals.period_items > 0
            ? formatMinSec(todayData.processing_stats.minutes_per_hour_audio)
            : "—"}
          sub={todayData?.processing_stats && todayData.totals.period_items > 0
            ? `${todayData.processing_stats.realtime_speed.toFixed(0)}x realtime`
            : "No timing data today"}
          variant="tertiary"
        />
        <QueueWaitCard queue={data.queue} />
      </div>

      {/* Daily Charts - Stacked Vertically */}
      {by_day.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {/* Daily Costs Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-sm font-medium text-gray-700">
              Daily Costs
            </h3>
            <DailyCostsChart data={by_day} />
            <div className="mt-3 flex gap-4">
              <Legend color={COLORS.assemblyai} label="Total cost" />
              <Legend color="#F97316" label="Avg $/min" />
            </div>
          </div>

          {/* Daily Transcriptions Chart */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-sm font-medium text-gray-700">
              Daily Transcriptions
            </h3>
            <DailyTranscriptionsChart data={by_day} />
            <div className="mt-3 flex gap-4">
              <Legend color="#3B82F6" label="Minutes transcribed" />
              <Legend color="#22C55E" label="Speed (min/hr)" />
            </div>
          </div>
        </div>
      )}

      {/* Provider Breakdown + All-Time */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Provider Breakdown */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">
            Cost by Provider
          </h3>
          <div className="space-y-3">
            {providers.map((p) => {
              const pct =
                totals.period_cost > 0
                  ? (p.cost / totals.period_cost) * 100
                  : 0;
              const color =
                p.key === "assemblyai_derived"
                  ? COLORS.assemblyai
                  : p.key === "claude_haiku"
                    ? COLORS.claude_haiku
                    : p.key === "gemini_flash"
                      ? COLORS.gemini_flash
                      : COLORS.logged;
              return (
                <div key={p.key}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm text-gray-700">
                        {PROVIDER_LABELS[p.key] || p.key}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        ${p.cost.toFixed(4)}
                      </span>
                      <span className="w-10 text-right text-xs text-gray-400">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.max(pct, 1)}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {p.minutes !== undefined &&
                      `${Math.round(p.minutes).toLocaleString()} min × $${p.rate_per_minute?.toFixed(4)}/min`}
                    {p.calls !== undefined && `${p.calls.toLocaleString()} API calls`}
                    {p.input_units !== undefined &&
                      p.input_units > 0 &&
                      ` · ${(p.input_units / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K in / ${((p.output_units || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K out`}
                  </p>
                </div>
              );
            })}
          </div>
          
          {/* Provider Dashboard Links */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">
              Cross-check with provider dashboards (~95% accuracy):
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://console.anthropic.com/settings/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Anthropic ↗
              </a>
              <span className="text-gray-300">·</span>
              <a
                href="https://www.assemblyai.com/app/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                AssemblyAI ↗
              </a>
              <span className="text-gray-300">·</span>
              <a
                href="https://platform.openai.com/usage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                OpenAI ↗
              </a>
              <span className="text-gray-300">·</span>
              <a
                href="https://www.perplexity.ai/settings/api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Perplexity ↗
              </a>
            </div>
          </div>
        </div>

        {/* All-Time Summary */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">
            All-Time Summary
          </h3>
          <div className="space-y-2.5">
            {/* Key metrics */}
            <SummaryRow
              label="True cost"
              value={`$${all_time.true_cost.toFixed(2)}`}
              highlight
            />
            <SummaryRow
              label="Unique minutes"
              value={`${Math.round(all_time.unique_minutes).toLocaleString()} min (${all_time.unique_transcripts} transcripts)`}
            />
            <SummaryRow
              label="True cost/min"
              value={`$${all_time.true_cost_per_minute.toFixed(4)}`}
            />
            <SummaryRow
              label="Cost per hour"
              value={`$${(all_time.true_cost_per_minute * 60).toFixed(2)}/hr`}
              highlight
            />
            <div className="my-2 h-px bg-gray-100" />
            {/* Cache efficiency */}
            <SummaryRow
              label="Library minutes"
              value={`${Math.round(all_time.total_minutes).toLocaleString()} min (${all_time.total_items} items)`}
            />
            <SummaryRow
              label="Cache hit rate"
              value={`${all_time.cache_hit_rate_pct.toFixed(1)}%`}
              highlight
            />
            <SummaryRow
              label="Effective $/min"
              value={`$${all_time.effective_cost_per_minute.toFixed(4)}`}
            />
            <div className="my-2 h-px bg-gray-100" />
            {/* Cost breakdown */}
            <SummaryRow
              label="AssemblyAI"
              value={`$${(all_time.unique_minutes * 0.002833).toFixed(2)}`}
            />
            <SummaryRow
              label="LLM costs"
              value={`$${all_time.logged_costs.toFixed(2)}`}
            />
          </div>
          {data_quality.items_missing_duration > 0 && (
            <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {data_quality.items_missing_duration} items missing duration —
              costs may be underestimated.
            </div>
          )}
          {data_quality.jobs_with_cost_tracking === 0 && (
            <div className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
              LLM cost tracking not yet active — only{" "}
              {all_time.logged_costs > 0 ? "4 legacy" : "0"} rows logged. Costs
              will improve once pipeline logging is enabled.
            </div>
          )}
        </div>
      </div>

      {/* Processing Performance */}
      {processing_stats && processing_stats.jobs_with_timing > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">
            Processing Performance
          </h3>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{Math.round(processing_stats.realtime_speed).toLocaleString()}x</span>
            <span className="text-sm text-gray-500">realtime</span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-500">~{formatMinSec(processing_stats.minutes_per_hour_audio)} per hour of audio</span>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-gray-500">Avg per Job</p>
              <p className="mt-0.5 text-sm font-medium text-gray-900">
                {processing_stats.avg_processing_seconds.toFixed(0)}s proc · {processing_stats.avg_audio_per_job_min.toFixed(0)} min audio
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Audio</p>
              <p className="mt-0.5 text-sm font-medium text-gray-900">
                {Math.round(processing_stats.total_audio_minutes).toLocaleString()} min
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Jobs Tracked</p>
              <p className="mt-0.5 text-sm font-medium text-gray-900">
                {processing_stats.jobs_with_timing.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rate info */}
      <p className="text-right text-xs text-gray-300">
        AssemblyAI rate: ${data.rates.assemblyai_per_minute}/min · Period:{" "}
        {data.period}
      </p>
    </div>
  );
}

/* ── Sub-components ── */

function StatCard({
  label,
  value,
  sub,
  variant = "secondary",
}: {
  label: string;
  value: string;
  sub: string;
  variant?: "primary" | "secondary" | "tertiary";
}) {
  const bgClass = variant === "primary"
    ? "bg-gradient-to-r from-blue-100 to-blue-200"
    : variant === "tertiary"
    ? "bg-gradient-to-r from-gray-50 to-gray-100 border-dashed"
    : "bg-gradient-to-r from-blue-50 to-blue-100";

  return (
    <div
      className={`flex flex-col rounded-lg border border-gray-200 ${bgClass} p-3 sm:p-4`}
    >
      <p className="text-[10px] sm:text-xs font-medium text-gray-600">{label}</p>
      <p className="mt-0.5 sm:mt-1 text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500 line-clamp-1">{sub}</p>
    </div>
  );
}

function PeriodQueueWaitCard({
  avgWaitSeconds,
  periodLabel,
}: {
  avgWaitSeconds?: number | null;
  periodLabel: string;
}) {
  if (avgWaitSeconds == null) {
    return (
      <div className="flex flex-col rounded-lg border border-gray-200 bg-gradient-to-r from-blue-100 to-blue-200 p-3 sm:p-4">
        <p className="text-[10px] sm:text-xs font-medium text-gray-600">Avg Queue Wait</p>
        <p className="mt-0.5 sm:mt-1 text-lg sm:text-2xl font-bold text-gray-400">—</p>
        <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500">No data {periodLabel}</p>
      </div>
    );
  }

  // Color coding: green (<30s), yellow (30-120s), red (>120s)
  let colorClass = "text-green-700";
  let bgClass = "bg-gradient-to-r from-green-100 to-green-200";
  let borderClass = "border-green-300";
  
  if (avgWaitSeconds >= 120) {
    colorClass = "text-red-700";
    bgClass = "bg-gradient-to-r from-red-100 to-red-200";
    borderClass = "border-red-300";
  } else if (avgWaitSeconds >= 30) {
    colorClass = "text-amber-700";
    bgClass = "bg-gradient-to-r from-amber-100 to-amber-200";
    borderClass = "border-amber-300";
  }

  const waitDisplay = avgWaitSeconds > 0 
    ? avgWaitSeconds >= 60 
      ? `${Math.floor(avgWaitSeconds / 60)}m ${Math.round(avgWaitSeconds % 60)}s`
      : `${Math.round(avgWaitSeconds)}s`
    : "0s";

  return (
    <div className={`flex flex-col rounded-lg border ${borderClass} ${bgClass} p-3 sm:p-4`}>
      <p className="text-[10px] sm:text-xs font-medium text-gray-600">Avg Queue Wait</p>
      <p className={`mt-0.5 sm:mt-1 text-lg sm:text-2xl font-bold ${colorClass}`}>{waitDisplay}</p>
      <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500 line-clamp-1">{periodLabel}</p>
    </div>
  );
}

function QueueWaitCard({
  queue,
}: {
  queue?: {
    active: number;
    waiting: number;
    max: number;
    avg_wait_seconds: number;
  };
}) {
  if (!queue) {
    return (
      <div className="flex flex-col rounded-lg border border-gray-200 border-dashed bg-gradient-to-r from-gray-50 to-gray-100 p-3 sm:p-4">
        <p className="text-[10px] sm:text-xs font-medium text-gray-600">Avg Queue Wait</p>
        <p className="mt-0.5 sm:mt-1 text-lg sm:text-2xl font-bold text-gray-400">—</p>
        <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500">No data</p>
      </div>
    );
  }

  const { active, waiting, max, avg_wait_seconds } = queue;
  
  // Color coding: green (<30s), yellow (30-120s), red (>120s)
  let colorClass = "text-green-600";
  let bgClass = "bg-gradient-to-r from-green-50 to-green-100";
  let borderClass = "border-green-200";
  
  if (avg_wait_seconds >= 120) {
    colorClass = "text-red-600";
    bgClass = "bg-gradient-to-r from-red-50 to-red-100";
    borderClass = "border-red-200";
  } else if (avg_wait_seconds >= 30) {
    colorClass = "text-amber-600";
    bgClass = "bg-gradient-to-r from-amber-50 to-amber-100";
    borderClass = "border-amber-200";
  }

  const waitDisplay = avg_wait_seconds > 0 
    ? avg_wait_seconds >= 60 
      ? `${Math.floor(avg_wait_seconds / 60)}m ${Math.round(avg_wait_seconds % 60)}s`
      : `${Math.round(avg_wait_seconds)}s`
    : "0s";

  return (
    <div className={`flex flex-col rounded-lg border ${borderClass} ${bgClass} p-3 sm:p-4`}>
      <p className="text-[10px] sm:text-xs font-medium text-gray-600">Avg Queue Wait</p>
      <p className={`mt-0.5 sm:mt-1 text-lg sm:text-2xl font-bold ${colorClass}`}>{waitDisplay}</p>
      <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500 line-clamp-1">
        {active}/{max} active · {waiting} waiting
      </p>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span
        className={`text-sm ${highlight ? "font-bold text-blue-700" : "font-medium text-gray-900"}`}
      >
        {value}
      </span>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <div
        className="h-2.5 w-2.5 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </div>
  );
}

function DailyCostsChart({
  data: rawData,
}: {
  data: {
    date: string;
    total_cost: number;
    minutes_transcribed: number;
  }[];
}) {
  if (rawData.length === 0) return null;

  const data = rawData.slice(-30);
  const maxCost = Math.max(...data.map((d) => d.total_cost), 0.01);
  // Calculate avg cost/min for each day (for line)
  const avgCosts = data.map((d) => 
    d.minutes_transcribed > 0 ? d.total_cost / d.minutes_transcribed : 0
  );
  const maxAvg = Math.max(...avgCosts, 0.001);
  const chartHeight = 160;

  // Build SVG line path for avg cost/min
  const linePoints = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const avg = d.minutes_transcribed > 0 ? d.total_cost / d.minutes_transcribed : 0;
    const y = 100 - (avg / maxAvg) * 100;
    return `${x},${y}`;
  }).join(" ");

  return (
    <div>
      {/* Y-axis labels */}
      <div className="mb-1 flex items-start justify-between">
        <span className="text-xs text-gray-400">${maxCost.toFixed(2)}</span>
        <span className="text-xs text-orange-400">${maxAvg.toFixed(4)}/min</span>
      </div>

      {/* Chart container */}
      <div className="relative" style={{ height: chartHeight }}>
        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-1">
          {data.map((d, i) => {
            const barH = (d.total_cost / maxCost) * chartHeight;
            const avgCost = d.minutes_transcribed > 0 ? d.total_cost / d.minutes_transcribed : 0;
            return (
              <div
                key={i}
                className="group relative flex flex-1 flex-col justify-end"
                style={{ minWidth: 0 }}
              >
                {/* Tooltip */}
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                  <p className="font-medium">{d.date}</p>
                  <p className="text-gray-300">${d.total_cost.toFixed(4)} total</p>
                  <p className="text-orange-300">${avgCost.toFixed(4)}/min avg</p>
                </div>
                <div
                  className="w-full rounded-t-sm transition-opacity group-hover:opacity-100"
                  style={{
                    height: Math.max(barH, 2),
                    backgroundColor: COLORS.assemblyai,
                    opacity: 0.7,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Line overlay for avg cost/min */}
        {data.length > 1 && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polyline
              fill="none"
              stroke="#F97316"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={linePoints}
              vectorEffect="non-scaling-stroke"
              style={{ transform: "scale(1, 1)" }}
            />
          </svg>
        )}
      </div>

      {/* X-axis */}
      <div className="mt-1 flex justify-between text-xs text-gray-400">
        <span>{data[0]?.date?.slice(5)}</span>
        {data.length > 2 && <span>{data[data.length - 1]?.date?.slice(5)}</span>}
      </div>
    </div>
  );
}

function DailyTranscriptionsChart({
  data: rawData,
}: {
  data: {
    date: string;
    minutes_transcribed: number;
    min_per_hour?: number | null;
  }[];
}) {
  if (rawData.length === 0) return null;

  const data = rawData.slice(-30);
  const maxMin = Math.max(...data.map((d) => d.minutes_transcribed), 1);
  const speeds = data.map((d) => d.min_per_hour || 0);
  const maxSpeed = Math.max(...speeds, 1);
  const chartHeight = 160;

  // Build SVG line path for speed
  const linePoints = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const y = 100 - ((d.min_per_hour || 0) / maxSpeed) * 100;
    return `${x},${y}`;
  }).join(" ");

  return (
    <div>
      {/* Y-axis labels */}
      <div className="mb-1 flex items-start justify-between">
        <span className="text-xs text-gray-400">{Math.round(maxMin).toLocaleString()} min</span>
        <span className="text-xs text-green-500">{Math.round(maxSpeed).toLocaleString()} min/hr</span>
      </div>

      {/* Chart container */}
      <div className="relative" style={{ height: chartHeight }}>
        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-1">
          {data.map((d, i) => {
            const barH = (d.minutes_transcribed / maxMin) * chartHeight;
            return (
              <div
                key={i}
                className="group relative flex flex-1 flex-col justify-end"
                style={{ minWidth: 0 }}
              >
                {/* Tooltip */}
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
                  <p className="font-medium">{d.date}</p>
                  <p className="text-gray-300">{Math.round(d.minutes_transcribed).toLocaleString()} min transcribed</p>
                  {d.min_per_hour != null && (
                    <p className="text-green-300">{d.min_per_hour.toFixed(1)} min/hr speed</p>
                  )}
                </div>
                <div
                  className="w-full rounded-t-sm transition-opacity group-hover:opacity-100"
                  style={{
                    height: Math.max(barH, 2),
                    backgroundColor: "#3B82F6",
                    opacity: 0.7,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Line overlay for speed */}
        {data.length > 1 && speeds.some((s) => s > 0) && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polyline
              fill="none"
              stroke="#22C55E"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={linePoints}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </div>

      {/* X-axis */}
      <div className="mt-1 flex justify-between text-xs text-gray-400">
        <span>{data[0]?.date?.slice(5)}</span>
        {data.length > 2 && <span>{data[data.length - 1]?.date?.slice(5)}</span>}
      </div>
    </div>
  );
}
